import { useCallback, useEffect, useRef, useState } from 'react';
import TopolPlugin from '@topol.io/editor';

import {
    getTopolOptions,
    LOAD_TIMEOUT,
    MAX_RETRIES,
    resolveTopolConfigForLoad,
    RETRY_DELAY,
} from '../topolConfig';

export const SAVE_MODES = {
    SAVE: 'save',
    PREVIEW: 'preview',
    SAVE_AS_TEMPLATE: 'save_as_template',
    DOWNLOAD: 'download',
};

export const STATUS = {
    INITIALIZING: 'initializing',
    READY: 'ready',
    ERROR: 'error',
};

export const useTopolEditor = ({
    containerRef,
    containerId,
    config,
    theme,
    onSave,
    onInit,
    onPreview,
    onSaveAsTemplate,
    onDownloadHtml,
    onError,
    isOpen,
    variables,
    userId,
    language,
    footerPreviewValues,
}) => {
    const topolInitialized = useRef(false);
    const loadTimeoutRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const currentConfigRef = useRef(config);
    const footerPreviewValuesRef = useRef(footerPreviewValues);
    const pendingSaveRef = useRef(null);
    const onSaveRef = useRef(onSave);
    const onInitRef = useRef(onInit);
    const onErrorRef = useRef(onError);
    const onPreviewRef = useRef(onPreview);
    const onSaveAsTemplateRef = useRef(onSaveAsTemplate);
    const onDownloadHtmlRef = useRef(onDownloadHtml);
    const variablesRef = useRef(variables);
    const loadedConfigRef = useRef(undefined);
    const loadedFooterKeyRef = useRef(undefined);
    useEffect(() => {
        onSaveRef.current = onSave;
        onInitRef.current = onInit;
        onErrorRef.current = onError;
        onPreviewRef.current = onPreview;
        onSaveAsTemplateRef.current = onSaveAsTemplate;
        onDownloadHtmlRef.current = onDownloadHtml;
    }, [onSave, onInit, onError, onPreview, onSaveAsTemplate, onDownloadHtml]);

    useEffect(() => {
        currentConfigRef.current = config;
    }, [config]);

    useEffect(() => {
        footerPreviewValuesRef.current = footerPreviewValues;
    }, [footerPreviewValues]);

    useEffect(() => {
        variablesRef.current = variables;
    }, [variables]);

    const [status, setStatus] = useState(STATUS.INITIALIZING);
    const [errorMessage, setErrorMessage] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [pendingSaveMode, setPendingSaveMode] = useState(null);

    const clearPendingSave = useCallback(() => {
        pendingSaveRef.current = null;
        setPendingSaveMode(null);
    }, []);

    const clearTimers = useCallback(() => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    const resetEditorStatus = useCallback(() => {
        setStatus(STATUS.INITIALIZING);
        setErrorMessage(null);
        setRetryCount(0);
    }, []);

    const destroy = useCallback(() => {
        if (!topolInitialized.current) return;
        try {
            TopolPlugin?.destroy?.();
        } catch (e) {
            // Ignore destroy errors
        }
        // Clear container content to ensure clean state
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
        }
        loadedConfigRef.current = undefined;
        loadedFooterKeyRef.current = undefined;
        clearPendingSave();
    }, [clearPendingSave, containerRef]);

    const loadTopolTemplate = useCallback(() => {
        if (!topolInitialized.current) {
            return;
        }

        try {
            TopolPlugin?.load?.(
                resolveTopolConfigForLoad(currentConfigRef.current, footerPreviewValuesRef.current),
            );
        } catch (error) {
            console.error('Error loading Topol template:', error);
        }
    }, []);

    const initialize = useCallback(
        (retryAttempt = 0) => {
            const hasContainer = !!containerRef.current;
            const hasTopolPlugin = TopolPlugin && typeof TopolPlugin.init === 'function';

            if (!hasContainer) {
                setStatus(STATUS.ERROR);
                setErrorMessage('Container not available');
                setRetryCount(retryAttempt);
                return;
            }

            if (!hasTopolPlugin) {
                if (retryAttempt < MAX_RETRIES) {
                    setStatus(STATUS.INITIALIZING);
                    setErrorMessage(null);
                    setRetryCount(retryAttempt + 1);

                    retryTimeoutRef.current = setTimeout(
                        () => initialize(retryAttempt + 1),
                        RETRY_DELAY,
                    );
                } else {
                    setStatus(STATUS.ERROR);
                    setErrorMessage('Topol plugin not available after retries');
                    setRetryCount(retryAttempt);
                }

                return;
            }

            resetEditorStatus();

            const options = getTopolOptions({
                containerId,
                footerPreviewValues: footerPreviewValuesRef.current,
                onSave: (json, html, previewHtml) => {
                    const mode = pendingSaveRef.current ?? SAVE_MODES.SAVE;
                    clearPendingSave();

                    const saveMethods = {
                        [SAVE_MODES.SAVE]: () => onSaveRef.current?.({ html, topolJson: json }),
                        [SAVE_MODES.PREVIEW]: () =>
                            onPreviewRef.current?.({ html: previewHtml ?? html }),
                        [SAVE_MODES.SAVE_AS_TEMPLATE]: () =>
                            onSaveAsTemplateRef.current?.({ html, topolJson: json }),
                        [SAVE_MODES.DOWNLOAD]: () =>
                            onDownloadHtmlRef.current?.({ html: previewHtml ?? html }),
                    };

                    saveMethods[mode]?.();
                },
                onInit: () => {
                    if (loadTimeoutRef.current) {
                        clearTimeout(loadTimeoutRef.current);
                        loadTimeoutRef.current = null;
                    }

                    setStatus(STATUS.INITIALIZING);
                    loadedConfigRef.current = currentConfigRef.current;
                    loadedFooterKeyRef.current = JSON.stringify(
                        footerPreviewValuesRef.current ?? {},
                    );
                    loadTopolTemplate();
                },
                onLoaded: () => {
                    setStatus(STATUS.READY);
                    setErrorMessage(null);
                    setRetryCount(0);
                    onInitRef.current?.();
                },
                onError: (error) => {
                    setStatus(STATUS.ERROR);
                    setErrorMessage(error?.message || 'Unknown error initializing editor');
                    setRetryCount(0);
                    onErrorRef.current?.(error);
                },
                theme,
                variables: variablesRef.current,
                userId,
                language,
            });

            try {
                TopolPlugin.init(options);
                topolInitialized.current = true;

                loadTimeoutRef.current = setTimeout(() => {
                    setStatus((prevStatus) => {
                        if (prevStatus !== STATUS.READY) {
                            setErrorMessage('Editor loading timeout - please refresh the page');
                            return STATUS.ERROR;
                        }
                        return prevStatus;
                    });
                }, LOAD_TIMEOUT);
            } catch (error) {
                setStatus(STATUS.ERROR);
                setErrorMessage(error?.message || 'Failed to initialize editor');
                setRetryCount(0);
            }
        },
        [
            clearPendingSave,
            containerId,
            containerRef,
            language,
            loadTopolTemplate,
            resetEditorStatus,
            theme,
            userId,
        ],
    );

    const retry = useCallback(() => {
        clearTimers();
        destroy();
        topolInitialized.current = false;
        resetEditorStatus();
        initialize();
    }, [clearTimers, destroy, initialize, resetEditorStatus]);

    const save = useCallback((mode = SAVE_MODES.SAVE) => {
        if (pendingSaveRef.current) {
            return;
        }

        pendingSaveRef.current = mode;
        setPendingSaveMode(mode);
        TopolPlugin?.save?.();
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        if (variables == null) return;
        if (topolInitialized.current) return;

        initialize();

        return () => {
            clearTimers();
            destroy();
            topolInitialized.current = false;
            resetEditorStatus();
        };
    }, [clearTimers, destroy, initialize, isOpen, resetEditorStatus, variables]);

    useEffect(() => {
        if (!isOpen || status !== STATUS.READY) {
            return;
        }

        const footerKey = JSON.stringify(footerPreviewValues ?? {});
        const configChanged = loadedConfigRef.current !== config;
        const footerChanged = loadedFooterKeyRef.current !== footerKey;

        if (!configChanged && !footerChanged) {
            return;
        }

        loadedConfigRef.current = config;
        loadedFooterKeyRef.current = footerKey;
        loadTopolTemplate();
    }, [config, footerPreviewValues, isOpen, loadTopolTemplate, status]);

    return {
        status,
        errorMessage,
        retryCount,
        pendingSaveMode,
        initialize,
        destroy,
        retry,
        save,
    };
};
